import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

enum DeltaColor {
  Green = 'statistic-delta_green',
  Red = 'statistic-delta_red',
  Grey = 'statistic-delta_grey',
}

@Component({
  selector: 'reviews-statistic-delta',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './statistic-delta.component.html',
  styleUrls: ['./statistic-delta.component.scss'],
  imports: [CommonModule, MatIcon],
})
export class StatisticDeltaComponent {
  @Input() value: number | null = null;
  @Input() delta: number | null = null; // Delta value (positive/negative/zero)
  @Input() isPercentage = false; // Flag to indicate if the delta is a percentage
  @Input() decimalPlaces = 0; // Number of decimal places for floating-point values
  @Input() alignDeltaCenter = false; //Aligns the delta value center
  @Input() alignDelta: 'inline' | 'bottom' = 'inline'; // Accepts 'inline' or 'bottom'

  // Format the value to handle floating-point numbers and percentage
  get formattedValue(): string {
    if (!this.isValid(this.value)) return '';
    const value = parseFloat(this.value.toFixed(this.decimalPlaces));
    return this.isPercentage ? `${value}%` : value.toString();
  }

  get alignStatisticContainer() {
    return {
      'align-delta-center': this.alignDeltaCenter, // Apply 'align-center' class if alignCenter is true
      'align-delta-bottom': this.alignDelta === 'bottom',
    };
  }

  // Format the delta value to handle floating-point numbers and percentage
  get formattedDelta(): string {
    if (!this.isValid(this.delta) || this.delta === 0) {
      return '';
    }
    const value = Math.abs(parseFloat(this.delta.toFixed(this.decimalPlaces)));
    return this.isPercentage ? `${value}%` : value.toString();
  }

  get deltaClass() {
    return {
      [DeltaColor.Grey]: !this.isValid(this.delta), // Default grey for invalid values
      [DeltaColor.Green]: this.isValid(this.delta) && this.delta > 0,
      [DeltaColor.Red]: this.isValid(this.delta) && this.delta < 0,
      'align-delta-bottom-margin': this.alignDelta === 'bottom', // Apply bottom alignment class
    };
  }

  // Handle null, undefined, or NaN
  private isValid(value: number): boolean {
    return value !== null && value !== undefined && !isNaN(value);
  }
}
